import {
  AlertTriangle,
  Edit3,
  Search,
  Shield,
  UserMinus,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { bugsnagClient } from "../bugsnag";
import { useAuth } from "../contexts/AuthContext";
import { ANALYTICS_EVENTS, trackEvent } from "../utils/analytics";
import api from "../utils/api";

// Add Team Member Modal
const AddTeamMemberModal = ({
  onClose,
  onAdd,
  isAdding,
  error: externalError,
  projectId,
}) => {
  const [formData, setFormData] = useState({
    userId: "",
    role: "member",
  });
  const [availableMembers, setAvailableMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchAvailableMembers = async () => {
      try {
        setIsLoading(true);
        const members = await api.fetchAvailableMembers(projectId);
        setAvailableMembers(members);
      } catch (error) {
        bugsnagClient.notify(error);
        console.error("Error fetching available members:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailableMembers();
  }, [projectId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.userId) {
      newErrors.userId = "Bitte wählen Sie ein Teammitglied aus";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    await onAdd(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          disabled={isAdding}
        >
          <X size={20} />
        </button>

        <h3 className="text-lg font-medium mb-4">Teammitglied hinzufügen</h3>

        {/* Display external error */}
        {externalError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{externalError}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teammitglied auswählen *
            </label>
            {isLoading ? (
              <div className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-500">
                Lade verfügbare Mitglieder...
              </div>
            ) : availableMembers.length === 0 ? (
              <div className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-500">
                Keine verfügbaren Mitglieder in der Organisation
              </div>
            ) : (
              <select
                value={formData.userId}
                onChange={(e) =>
                  setFormData({ ...formData, userId: e.target.value })
                }
                className={`w-full border rounded-md px-3 py-2 ${
                  errors.userId ? "border-red-500" : "border-gray-300"
                }`}
                disabled={isAdding}
              >
                <option value="">Bitte wählen Sie ein Mitglied aus</option>
                {availableMembers.map((member) => (
                  <option key={member._id} value={member._id}>
                    {member.name} ({member.email}) -{" "}
                    {member.organizationRole === "admin" ? "Admin" : "Mitglied"}
                  </option>
                ))}
              </select>
            )}
            {errors.userId && (
              <p className="text-red-500 text-xs mt-1">{errors.userId}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Projektrolle
            </label>
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              disabled={isAdding}
            >
              <option value="member">Projektmitglied</option>
              <option value="manager">Projektmanager</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Projektmanager können das Team und Einstellungen verwalten
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isAdding}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={isAdding || availableMembers.length === 0 || isLoading}
            >
              {isAdding ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white inline mr-2"></div>
                  Hinzufügen...
                </>
              ) : (
                "Hinzufügen"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Role Change Modal
const RoleChangeModal = ({ isOpen, member, onClose, onConfirm, isLoading }) => {
  const [newRole, setNewRole] = useState(member?.projectRole || "member");

  useEffect(() => {
    if (member) {
      setNewRole(member.projectRole);
    }
  }, [member]);

  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
        <h3 className="text-lg font-medium mb-4">Rolle ändern</h3>
        <p className="text-gray-600 mb-4">
          Rolle für <strong>{member.email}</strong> ändern:
        </p>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Neue Projektrolle
          </label>
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            disabled={isLoading}
          >
            <option value="member">Projektmitglied</option>
            <option value="manager">Projektmanager</option>
          </select>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Abbrechen
          </button>
          <button
            onClick={() => onConfirm(newRole)}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            disabled={isLoading || newRole === member.projectRole}
          >
            {isLoading ? "Ändern..." : "Ändern"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Confirmation Dialog
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  isLoading,
  type = "danger",
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
        <h3 className="text-lg font-medium mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2 rounded-md text-white disabled:opacity-50 ${
              type === "danger"
                ? "bg-red-600 hover:bg-red-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
            disabled={isLoading}
          >
            {isLoading ? "Wird verarbeitet..." : "Bestätigen"}
          </button>
        </div>
      </div>
    </div>
  );
};

const TeamManagement = ({ projectId, userRole, isGlobalAdmin }) => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  // Role change states
  const [roleChangeModal, setRoleChangeModal] = useState({
    isOpen: false,
    member: null,
  });
  const [isChangingRole, setIsChangingRole] = useState(false);

  // Remove member states
  const [removeConfirm, setRemoveConfirm] = useState({
    isOpen: false,
    member: null,
  });
  const [isRemoving, setIsRemoving] = useState(false);

  // Check if user can manage team based on organization role and project role
  const getCurrentUserProjectRole = () => {
    if (!user?.email || !teamMembers.length) return null;
    const currentUser = teamMembers.find(
      (member) => member.email === user.email,
    );
    return currentUser?.projectRole || null;
  };

  const currentUserProjectRole = getCurrentUserProjectRole();

  const canManageTeam =
    isGlobalAdmin ||
    userRole === "admin" ||
    currentUserProjectRole === "owner" ||
    currentUserProjectRole === "manager";

  // Fetch team members
  const fetchTeamMembers = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.fetchProjectTeam(projectId);
      setTeamMembers(response.teamMembers || []);
    } catch (err) {
      bugsnagClient.notify(err);
      console.error("Error fetching team members:", err);
      setError(err.message || "Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, [projectId]);

  // Add team member
  const handleAddMember = async (memberData) => {
    setIsAdding(true);
    setAddError(null);

    try {
      await api.addProjectTeamMember(projectId, memberData);
      trackEvent(ANALYTICS_EVENTS.PROJECT_TEAM_MEMBER_ADDED, {
        projectId,
        role: memberData.role,
      });
      await fetchTeamMembers();
      setShowAddModal(false);
      setAddError(null);
    } catch (err) {
      bugsnagClient.notify(err);
      console.error("Error adding team member:", err);
      setAddError(err.message || "Fehler beim Hinzufügen des Teammitglieds");
    } finally {
      setIsAdding(false);
    }
  };

  // Change member role
  const handleRoleChange = async (newRole) => {
    const { member } = roleChangeModal;
    if (!member) return;

    setIsChangingRole(true);
    try {
      await api.updateProjectTeamMemberRole(projectId, member._id, newRole);
      trackEvent(ANALYTICS_EVENTS.PROJECT_TEAM_MEMBER_ROLE_CHANGED, {
        projectId,
        newRole,
      });
      await fetchTeamMembers();
      setRoleChangeModal({ isOpen: false, member: null });
    } catch (err) {
      bugsnagClient.notify(err);
      console.error("Error updating member role:", err);
      setError(err.message || "Failed to update member role");
    } finally {
      setIsChangingRole(false);
    }
  };

  // Remove team member
  const handleRemoveMember = async () => {
    const { member } = removeConfirm;
    if (!member) return;

    setIsRemoving(true);
    try {
      await api.removeProjectTeamMember(projectId, member._id);
      trackEvent(ANALYTICS_EVENTS.PROJECT_TEAM_MEMBER_REMOVED, { projectId });
      await fetchTeamMembers();
      setRemoveConfirm({ isOpen: false, member: null });
    } catch (err) {
      bugsnagClient.notify(err);
      console.error("Error removing team member:", err);
      setError(err.message || "Failed to remove team member");
    } finally {
      setIsRemoving(false);
    }
  };

  // Filter team members based on search
  const filteredMembers = teamMembers.filter(
    (member) =>
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Get role display info
  const getRoleInfo = (orgRole, projectRole) => {
    if (orgRole === "admin") {
      return {
        label: "Organisationsadmin",
        color: "bg-purple-100 text-purple-800",
        icon: Shield,
      };
    }
    if (projectRole === "manager") {
      return {
        label: "Projektmanager",
        color: "bg-blue-100 text-blue-800",
        icon: Shield,
      };
    }
    return {
      label: "Projektmitglied",
      color: "bg-gray-100 text-gray-800",
      icon: Users,
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <Users className="h-6 w-6 text-gray-500" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Projektteam</h2>
            <p className="text-sm text-gray-500">
              {teamMembers.length}{" "}
              {teamMembers.length === 1 ? "Mitglied" : "Mitglieder"}
            </p>
          </div>
        </div>

        {canManageTeam && (
          <button
            onClick={() => {
              setShowAddModal(true);
              setAddError(null);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Mitglied hinzufügen
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Team durchsuchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Team Members List */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
        {filteredMembers.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchTerm
                ? "Keine Mitglieder gefunden"
                : "Noch keine Teammitglieder"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm
                ? "Versuchen Sie einen anderen Suchbegriff"
                : canManageTeam
                  ? "Fügen Sie Teammitglieder hinzu, um zu beginnen"
                  : "Noch keine Teammitglieder hinzugefügt"}
            </p>
          </div>
        ) : (
          filteredMembers.map((member) => {
            const roleInfo = getRoleInfo(
              member.organizationRole,
              member.projectRole,
            );
            const RoleIcon = roleInfo.icon;

            return (
              <div key={member._id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700">
                          {member.name
                            ? member.name.charAt(0).toUpperCase()
                            : member.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {member.name || "Unbekannt"}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {member.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {/* Role Badge */}
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleInfo.color}`}
                    >
                      <RoleIcon className="h-3 w-3 mr-1" />
                      {roleInfo.label}
                    </span>

                    {/* Actions */}
                    {canManageTeam && member.organizationRole !== "admin" && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() =>
                            setRoleChangeModal({ isOpen: true, member })
                          }
                          className="text-blue-600 hover:text-blue-900"
                          title="Rolle ändern"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() =>
                            setRemoveConfirm({ isOpen: true, member })
                          }
                          className="text-red-600 hover:text-red-900"
                          title="Aus Team entfernen"
                        >
                          <UserMinus className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <AddTeamMemberModal
          onClose={() => {
            setShowAddModal(false);
            setAddError(null);
          }}
          onAdd={handleAddMember}
          isAdding={isAdding}
          error={addError}
          projectId={projectId}
        />
      )}

      {/* Role Change Modal */}
      <RoleChangeModal
        isOpen={roleChangeModal.isOpen}
        member={roleChangeModal.member}
        onClose={() => setRoleChangeModal({ isOpen: false, member: null })}
        onConfirm={handleRoleChange}
        isLoading={isChangingRole}
      />

      {/* Remove Confirmation */}
      <ConfirmDialog
        isOpen={removeConfirm.isOpen}
        title="Teammitglied entfernen"
        message={`Sind Sie sicher, dass Sie ${
          removeConfirm.member?.name || removeConfirm.member?.email
        } aus dem Projektteam entfernen möchten?`}
        onConfirm={handleRemoveMember}
        onCancel={() => setRemoveConfirm({ isOpen: false, member: null })}
        isLoading={isRemoving}
        type="danger"
      />
    </div>
  );
};

export default TeamManagement;
